/** Wallet, points, card, and gift-card domain events — catalog 1.3.0 additions. */
import {
  asEventRecord,
  defineEvent,
  optionalNumber,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface WalletCreatedPayload {
  walletId: string;
  orgId: string;
  customerId: string;
}

export interface PointsEventPayload {
  accountId: string;
  orgId: string;
  points: number;
  multiplierBps?: number;
  reason: string;
  orderId?: string;
}

export interface WalletCardEventPayload {
  cardId: string;
  orgId: string;
  type: WalletCardType;
  status: WalletCardStatus;
}

export type WalletCardType =
  | 'membership'
  | 'gift_card'
  | 'loyalty'
  | 'access'
  | 'login'
  | 'payment_token';

export type WalletCardStatus = 'active' | 'frozen' | 'expired' | 'cancelled';

export interface GiftCardEventPayload {
  cardId: string;
  orgId: string;
  amountMinor?: number;
  orderId?: string;
  balanceAfter?: number;
}

export interface WalletBalanceDriftPayload {
  walletId: string;
  orgId: string;
  computedBalance: number;
  storedBalance: number;
}

const integer = (value: unknown, field: string, context: string): number => {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error(`${context}: field "${field}" must be an integer`);
  }
  return value;
};

const optionalInteger = (value: unknown, field: string, context: string): number | undefined => {
  const parsed = optionalNumber(value, field, context);
  if (parsed !== undefined && !Number.isInteger(parsed)) {
    throw new Error(`${context}: field "${field}" must be an integer`);
  }
  return parsed;
};

const cardTypes: readonly WalletCardType[] = [
  'membership',
  'gift_card',
  'loyalty',
  'access',
  'login',
  'payment_token',
];

const cardStatuses: readonly WalletCardStatus[] = ['active', 'frozen', 'expired', 'cancelled'];

const requireChoice = <T extends string>(
  value: unknown,
  values: readonly T[],
  field: string,
  context: string,
): T => {
  if (typeof value !== 'string' || !values.includes(value as T)) {
    throw new Error(`${context}: field "${field}" must be one of ${values.join(', ')}`);
  }
  return value as T;
};

const walletCreatedSchema: EventSchema<WalletCreatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'wallets:wallet-created');
    return {
      walletId: requireString(record.walletId, 'walletId', 'wallets:wallet-created'),
      orgId: requireString(record.orgId, 'orgId', 'wallets:wallet-created'),
      customerId: requireString(record.customerId, 'customerId', 'wallets:wallet-created'),
    };
  },
};

const pointsSchema = (type: string): EventSchema<PointsEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    const multiplierBps = optionalInteger(record.multiplierBps, 'multiplierBps', type);
    const orderId = optionalString(record.orderId, 'orderId', type);
    return {
      accountId: requireString(record.accountId, 'accountId', type),
      orgId: requireString(record.orgId, 'orgId', type),
      points: integer(record.points, 'points', type),
      reason: requireString(record.reason, 'reason', type),
      ...(multiplierBps === undefined ? {} : { multiplierBps }),
      ...(orderId === undefined ? {} : { orderId }),
    };
  },
});

const cardSchema = (type: string): EventSchema<WalletCardEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    return {
      cardId: requireString(record.cardId, 'cardId', type),
      orgId: requireString(record.orgId, 'orgId', type),
      type: requireChoice(record.type, cardTypes, 'type', type),
      status: requireChoice(record.status, cardStatuses, 'status', type),
    };
  },
});

const giftCardSchema = (type: string): EventSchema<GiftCardEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    const amountMinor = optionalInteger(record.amountMinor, 'amountMinor', type);
    const orderId = optionalString(record.orderId, 'orderId', type);
    const balanceAfter = optionalInteger(record.balanceAfter, 'balanceAfter', type);
    return {
      cardId: requireString(record.cardId, 'cardId', type),
      orgId: requireString(record.orgId, 'orgId', type),
      ...(amountMinor === undefined ? {} : { amountMinor }),
      ...(orderId === undefined ? {} : { orderId }),
      ...(balanceAfter === undefined ? {} : { balanceAfter }),
    };
  },
});

const balanceDriftSchema: EventSchema<WalletBalanceDriftPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'wallets:balance-drift-detected');
    return {
      walletId: requireString(record.walletId, 'walletId', 'wallets:balance-drift-detected'),
      orgId: requireString(record.orgId, 'orgId', 'wallets:balance-drift-detected'),
      computedBalance: integer(
        record.computedBalance,
        'computedBalance',
        'wallets:balance-drift-detected',
      ),
      storedBalance: integer(
        record.storedBalance,
        'storedBalance',
        'wallets:balance-drift-detected',
      ),
    };
  },
};

const definePointsEvent = (
  type:
    | 'wallets:points-earned'
    | 'wallets:points-spent'
    | 'wallets:points-expired'
    | 'wallets:points-adjusted',
  description: string,
): EventDefinition<PointsEventPayload> =>
  defineEvent<PointsEventPayload>({
    type,
    version: 1,
    schema: pointsSchema(type),
    auditable: true,
    description,
    since: '1.3.0',
  });

const defineCardEvent = (
  type: 'wallets:card-created' | 'wallets:card-status-changed',
  description: string,
): EventDefinition<WalletCardEventPayload> =>
  defineEvent<WalletCardEventPayload>({
    type,
    version: 1,
    schema: cardSchema(type),
    auditable: true,
    description,
    since: '1.3.0',
  });

const defineGiftCardEvent = (
  type:
    | 'wallets:giftcard-issued'
    | 'wallets:giftcard-sold'
    | 'wallets:giftcard-redeemed'
    | 'wallets:giftcard-refunded'
    | 'wallets:giftcard-expired',
  description: string,
): EventDefinition<GiftCardEventPayload> =>
  defineEvent<GiftCardEventPayload>({
    type,
    version: 1,
    schema: giftCardSchema(type),
    auditable: true,
    description,
    since: '1.3.0',
  });

export const WALLETS_WALLET_CREATED_EVENT: EventDefinition<WalletCreatedPayload> = defineEvent({
  type: 'wallets:wallet-created',
  version: 1,
  schema: walletCreatedSchema,
  auditable: true,
  description: 'A customer wallet was created for an organization.',
  since: '1.3.0',
});

export const WALLETS_POINTS_EARNED_EVENT = definePointsEvent(
  'wallets:points-earned',
  'Points were earned into an account.',
);
export const WALLETS_POINTS_SPENT_EVENT = definePointsEvent(
  'wallets:points-spent',
  'Points were spent from an account.',
);
export const WALLETS_POINTS_EXPIRED_EVENT = definePointsEvent(
  'wallets:points-expired',
  'Points expired from an account.',
);
export const WALLETS_POINTS_ADJUSTED_EVENT = definePointsEvent(
  'wallets:points-adjusted',
  'An account point balance was adjusted.',
);
export const WALLETS_CARD_CREATED_EVENT = defineCardEvent(
  'wallets:card-created',
  'A tokenized wallet card was created.',
);
export const WALLETS_CARD_STATUS_CHANGED_EVENT = defineCardEvent(
  'wallets:card-status-changed',
  'A wallet card status changed.',
);
export const WALLETS_GIFTCARD_ISSUED_EVENT = defineGiftCardEvent(
  'wallets:giftcard-issued',
  'A gift card was issued.',
);
export const WALLETS_GIFTCARD_SOLD_EVENT = defineGiftCardEvent(
  'wallets:giftcard-sold',
  'A gift card was sold.',
);
export const WALLETS_GIFTCARD_REDEEMED_EVENT = defineGiftCardEvent(
  'wallets:giftcard-redeemed',
  'A gift card balance was redeemed.',
);
export const WALLETS_GIFTCARD_REFUNDED_EVENT = defineGiftCardEvent(
  'wallets:giftcard-refunded',
  'A gift card sale or redemption was refunded.',
);
export const WALLETS_GIFTCARD_EXPIRED_EVENT = defineGiftCardEvent(
  'wallets:giftcard-expired',
  'A gift card expired.',
);

export const WALLETS_BALANCE_DRIFT_DETECTED_EVENT: EventDefinition<WalletBalanceDriftPayload> =
  defineEvent({
    type: 'wallets:balance-drift-detected',
    version: 1,
    schema: balanceDriftSchema,
    auditable: true,
    description: 'A wallet ledger balance differs from its stored projection.',
    since: '1.3.0',
  });

export const WALLETS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [WALLETS_WALLET_CREATED_EVENT.type]: WALLETS_WALLET_CREATED_EVENT,
  [WALLETS_POINTS_EARNED_EVENT.type]: WALLETS_POINTS_EARNED_EVENT,
  [WALLETS_POINTS_SPENT_EVENT.type]: WALLETS_POINTS_SPENT_EVENT,
  [WALLETS_POINTS_EXPIRED_EVENT.type]: WALLETS_POINTS_EXPIRED_EVENT,
  [WALLETS_POINTS_ADJUSTED_EVENT.type]: WALLETS_POINTS_ADJUSTED_EVENT,
  [WALLETS_CARD_CREATED_EVENT.type]: WALLETS_CARD_CREATED_EVENT,
  [WALLETS_CARD_STATUS_CHANGED_EVENT.type]: WALLETS_CARD_STATUS_CHANGED_EVENT,
  [WALLETS_GIFTCARD_ISSUED_EVENT.type]: WALLETS_GIFTCARD_ISSUED_EVENT,
  [WALLETS_GIFTCARD_SOLD_EVENT.type]: WALLETS_GIFTCARD_SOLD_EVENT,
  [WALLETS_GIFTCARD_REDEEMED_EVENT.type]: WALLETS_GIFTCARD_REDEEMED_EVENT,
  [WALLETS_GIFTCARD_REFUNDED_EVENT.type]: WALLETS_GIFTCARD_REFUNDED_EVENT,
  [WALLETS_GIFTCARD_EXPIRED_EVENT.type]: WALLETS_GIFTCARD_EXPIRED_EVENT,
  [WALLETS_BALANCE_DRIFT_DETECTED_EVENT.type]: WALLETS_BALANCE_DRIFT_DETECTED_EVENT,
};
